/**
 * DocumentGeneratorService
 * 
 * Transforms PlanBuilder progress into structured documents:
 * 1. Assessment Report - VMI scores, quadrant placement, recommendations
 * 2. Succession Plan Document - Prescriptive content organized by section
 * 
 * Also supports specialized plan types:
 * - catastrophic: Uses catastrophic-plan-generator.js
 * - continuity: Uses bcp-template-generator.js
 */

// Import specialized generators for non-succession plan types
import generateCatastrophicPlanDocument from '../../Succession/catastrophic-plan-generator';
import { generateBCPDocument } from '../../Succession/bcp-template-generator';
import { mapPlanBuilderToCatastrophicFormat } from './catastrophic-data-mapper';
import { mapPlanBuilderToBCPFormat } from './continuity-data-mapper';

// Output section configuration matching YAML output_sections
const SECTION_CONFIG = {
  personal_snapshot: {
    title: "Personal Snapshot",
    order: 1,
    description: "Your personal readiness, motivations, and post-exit vision"
  },
  financial_snapshot: {
    title: "Financial Snapshot",
    order: 2,
    description: "Your financial capability and wealth goals"
  },
  business_overview: {
    title: "Business Overview",
    order: 3,
    description: "Strategic direction, value drivers, and operational readiness"
  },
  successor_profile: {
    title: "Successor Profile",
    order: 4,
    description: "Your ideal successor and selection criteria"
  },
  transition_timeline: {
    title: "Transition Timeline",
    order: 5,
    description: "Key milestones and target dates"
  },
  deal_structure: {
    title: "Deal Structure",
    order: 6,
    description: "Financial terms, tax considerations, and sale expectations"
  },
  communication_plan: {
    title: "Communication Plan",
    order: 7,
    description: "How you'll communicate your transition to stakeholders"
  }
};

// Category to VMI mapping
const CATEGORY_TO_VMI = {
  personal_goals: 'Identify',
  financial_planning: 'Identify',
  risk_protection: 'Protect',
  business_value: 'Build',
  operations: 'Manage',
  successor: 'Identify',
  exit_execution: 'Harvest'
};

// Gap action slugs to human-readable recommendations
const GAP_ACTION_RECOMMENDATIONS = {
  financial_plan: "Create a comprehensive post-exit personal financial plan with your financial advisor",
  emotional_readiness: "Work with a coach or counselor to explore your identity beyond the business",
  protect_estate: "Consult with an estate planning attorney to update wills, powers of attorney, and shareholder agreements",
  build_value: "Implement initiatives to increase recurring revenue and client diversification",
  improve_operations: "Document key business processes and reduce owner dependency in daily operations",
  valuation: "Schedule a formal business valuation to understand your current market value",
  find_successor: "Begin actively identifying and evaluating potential internal or external successors",
  communication_plan: "Develop a client communication strategy for announcing your succession"
};

/**
 * Generate Executive Summary paragraph from plan sections
 */
const generateExecutiveSummary = (sections, userName, companyName) => {
  // Extract key data points from sections to build summary
  const personalSection = sections.personal_snapshot;
  const financialSection = sections.financial_snapshot;
  const businessSection = sections.business_overview;
  const successorSection = sections.successor_profile;
  const timelineSection = sections.transition_timeline;
  
  // Find specific answers by looking for key question patterns
  const findAnswer = (section, patterns) => {
    if (!section?.questions) return null;
    for (const q of section.questions) {
      for (const pattern of patterns) {
        if (q.questionText?.toLowerCase().includes(pattern.toLowerCase())) {
          return q.formattedAnswer;
        }
      }
    }
    return null;
  };
  
  // Extract key insights
  const targetDate = findAnswer(timelineSection, ['target date', 'exit date', 'when do you']);
  const exitReason = findAnswer(personalSection, ['motivation', 'why are you', 'primary reason']);
  const practiceValue = findAnswer(financialSection, ['worth', 'value', 'practice is worth']);
  const successorType = findAnswer(successorSection, ['type of successor', 'ideal successor', 'who would']);
  
  // Build summary paragraph
  let summary = `This succession plan documents ${userName}'s strategy for transitioning ${companyName}. `;
  
  if (exitReason) {
    summary += `The primary motivation for this transition is: ${exitReason.toLowerCase().replace(/\.$/, '')}. `;
  }
  
  if (practiceValue) {
    summary += `The practice is currently valued at approximately ${practiceValue}. `;
  }
  
  if (successorType) {
    summary += `The preferred successor profile is: ${successorType.toLowerCase().replace(/\.$/, '')}. `;
  }
  
  if (targetDate) {
    summary += `The target completion date for this transition is ${targetDate}. `;
  }
  
  // Add section count
  const sectionCount = Object.values(sections).filter(s => s.questions?.length > 0).length;
  summary += `This plan covers ${sectionCount} key areas to ensure a smooth and successful transition.`;
  
  return summary;
};

/**
 * Format answer value based on widget type
 */
const formatAnswerValue = (answer, widgetType) => {
  if (!answer) return '';
  
  const value = answer.value;
  const formatted = answer.formatted;
  
  // Use pre-formatted value if available
  if (formatted) return formatted;
  
  if (!value) return '';
  
  switch (widgetType) {
    case 'currency':
      if (typeof value === 'number') {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
      }
      return value;
      
    case 'percentage':
      return `${value}%`;
      
    case 'date':
      try {
        return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      } catch {
        return value;
      }
      
    case 'yesno':
    case 'yes-no':
      return value === 'yes' ? 'Yes' : value === 'no' ? 'No' : value;
      
    case 'checklist':
    case 'multiple-choice':
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return value;
      
    case 'rating':
      return `${value}/6`;
      
    case 'contact':
      if (typeof value === 'object') {
        const parts = [];
        if (value.name) parts.push(value.name);
        if (value.title) parts.push(value.title);
        if (value.email) parts.push(value.email);
        if (value.phone) parts.push(value.phone);
        return parts.join('\n');
      }
      return value;
      
    default:
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      return String(value);
  }
};

/**
 * Group questions by their output_section
 */
const groupBySection = (progress) => {
  const sections = {};
  
  // Initialize all sections
  Object.keys(SECTION_CONFIG).forEach(key => {
    sections[key] = {
      ...SECTION_CONFIG[key],
      key,
      questions: []
    };
  });
  
  // Add uncategorized section for questions without output_section
  sections.uncategorized = {
    title: "Additional Information",
    order: 99,
    key: 'uncategorized',
    questions: []
  };
  
  const { planTemplate, userPlanResponses } = progress;
  
  // Create answer map
  const answerMap = {};
  if (userPlanResponses) {
    userPlanResponses.forEach(r => {
      answerMap[r.planQuestionId] = r;
    });
  }
  
  // Iterate through all questions and group by output_section
  if (planTemplate?.planModules) {
    planTemplate.planModules.forEach(module => {
      module.planSections?.forEach(section => {
        section.planQuestions?.forEach(question => {
          const response = answerMap[question.id];
          if (response) {
            const outputSection = question.outputSection || 'uncategorized';
            const sectionKey = sections[outputSection] ? outputSection : 'uncategorized';
            
            sections[sectionKey].questions.push({
              ...question,
              answer: response.answer,
              formattedAnswer: formatAnswerValue(response.answer, question.widgetType),
              module: module.title,
              originalSection: section.title
            });
          }
        });
      });
    });
  }
  
  return sections;
};

/**
 * Calculate score for a single question based on widget type
 */
const calculateQuestionScore = (question, value) => {
  if (value === null || value === undefined) return 0;
  
  if (question.widgetType === 'rating') {
    return parseInt(value) || 0;
  } else if (question.widgetType === 'yesno') {
    return value === 'yes' ? 6 : 1;
  } else if (question.widgetType === 'multiple-choice') {
    const options = question.options?.options || [];
    const index = options.indexOf(value);
    if (index >= 0) {
      return Math.round(((index + 1) / options.length) * 6);
    }
  }
  return 0;
};

/**
 * Calculate assessment scores from responses using the 12-question diagnostic
 * This matches the ReadinessScoreHero and WizardOverlay calculations
 */
const calculateAssessmentScores = (progress) => {
  const gaps = [];
  
  // 12-question diagnostic: owner_q1-q6 (owner readiness), owner_q7-q12 (business attractiveness)
  const ownerAnswers = Array(6).fill(0);      // q1-q6
  const businessAnswers = Array(6).fill(0);   // q7-q12
  
  // VMI category scores (still used for category breakdown display)
  const scores = {
    identify: { total: 0, count: 0, max: 0 },
    protect: { total: 0, count: 0, max: 0 },
    build: { total: 0, count: 0, max: 0 },
    harvest: { total: 0, count: 0, max: 0 },
    manage: { total: 0, count: 0, max: 0 }
  };
  
  const { planTemplate, userPlanResponses } = progress;
  
  const answerMap = {};
  if (userPlanResponses) {
    userPlanResponses.forEach(r => {
      answerMap[r.planQuestionId] = r;
    });
  }
  
  if (planTemplate?.planModules) {
    planTemplate.planModules.forEach(module => {
      module.planSections?.forEach(section => {
        section.planQuestions?.forEach(question => {
          const response = answerMap[question.id];
          const value = response?.answer?.value;
          
          // Extract diagnostic key from metadata for the 12-question scoring
          const diagnosticKey = question.metadata?.diagnostic_key;
          if (diagnosticKey && response) {
            const questionScore = calculateQuestionScore(question, value);
            
            // owner_q1-q6 go into ownerAnswers
            if (diagnosticKey.match(/^owner_q[1-6]$/)) {
              const idx = parseInt(diagnosticKey.replace('owner_q', '')) - 1;
              ownerAnswers[idx] = questionScore;
            }
            // owner_q7-q12 go into businessAnswers  
            else if (diagnosticKey.match(/^owner_q(7|8|9|10|11|12)$/)) {
              const idx = parseInt(diagnosticKey.replace('owner_q', '')) - 7;
              businessAnswers[idx] = questionScore;
            }
          }
          
          // Process assessment questions for VMI category breakdown and gaps
          if (question.purpose === 'assessment' || question.purpose === 'both') {
            const category = question.category;
            const vmiCategory = CATEGORY_TO_VMI[category]?.toLowerCase();
            
            if (vmiCategory && scores[vmiCategory] && response) {
              const questionScore = calculateQuestionScore(question, value);
              scores[vmiCategory].total += questionScore;
              scores[vmiCategory].max += 6;
              scores[vmiCategory].count += 1;
              
              // Check for gap triggers
              if (question.gapAction && response.answer) {
                const condition = question.gapAction.condition;
                const answerValue = response.answer.value;
                const formattedAnswer = formatAnswerValue(response.answer, question.widgetType);
                
                if (
                  (condition === 'no' && answerValue === 'no') ||
                  (condition === answerValue) ||
                  (condition?.startsWith('<') && parseInt(answerValue) < parseInt(condition.slice(1)))
                ) {
                  gaps.push({
                    question: question.questionText,
                    answer: formattedAnswer,
                    action: question.gapAction.action,
                    priority: question.gapAction.priority,
                    category: vmiCategory
                  });
                }
              }
            }
          }
        });
      });
    });
  }
  
  // Calculate readiness using the 12-question diagnostic (same as ownerDiagnosticScoring.js)
  const ownerScores = ownerAnswers.filter(v => v > 0);
  const businessScores = businessAnswers.filter(v => v > 0);
  
  const ownerReadinessScore = ownerScores.length > 0 
    ? Math.round((ownerScores.reduce((a, b) => a + b, 0) / (ownerScores.length * 6)) * 100)
    : 0;
  
  const buyerAttractivenessScore = businessScores.length > 0
    ? Math.round((businessScores.reduce((a, b) => a + b, 0) / (businessScores.length * 6)) * 100)
    : 0;
  
  const overallReadiness = Math.round((ownerReadinessScore + buyerAttractivenessScore) / 2);
  
  // Calculate VMI category percentages (for category breakdown display)
  const vmiScores = {};
  Object.keys(scores).forEach(key => {
    const s = scores[key];
    vmiScores[key] = {
      score: s.max > 0 ? Math.round((s.total / s.max) * 6 * 10) / 10 : 0,
      percentage: s.max > 0 ? Math.round((s.total / s.max) * 100) : 0
    };
  });
  
  return {
    vmiScores,
    overallReadiness,
    ownerReadinessScore,
    buyerAttractivenessScore,
    gaps: gaps.sort((a, b) => {
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      return (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
    })
  };
};

/**
 * Generate Assessment Report (Markdown)
 * @param {Object} progress - Plan progress with template and responses
 * @param {string} userName - User's name for the report
 * @param {Object} storedScores - Optional pre-calculated scores from backend
 * @param {number} storedScores.vmiReadinessScore - Overall readiness score (0-100)
 * @param {Array<number>} storedScores.diagOwnerAnswers - Owner answers array [q1-q6]
 * @param {Array<number>} storedScores.diagBusinessAnswers - Business answers array [q7-q12]
 */
export const generateAssessmentReport = (progress, userName = 'User', storedScores = null) => {
  const assessment = calculateAssessmentScores(progress);
  
  // Use stored scores from backend if available (source of truth)
  let overallReadiness = assessment.overallReadiness;
  if (storedScores?.vmiReadinessScore != null && storedScores.vmiReadinessScore > 0) {
    overallReadiness = storedScores.vmiReadinessScore;
  }
  
  // Header section (marked for centering)
  let md = `<!-- HEADER_START -->\n`;
  md += `# Succession Readiness Assessment\n\n`;
  md += `### ${userName}\n\n`;
  md += `**Date:** ${new Date().toLocaleDateString()}\n`;
  md += `<!-- HEADER_END -->\n\n`;
  md += `---\n\n`;
  
  // Overall Readiness with visual indicator
  const readinessEmoji = overallReadiness >= 80 ? '🟢' : 
                         overallReadiness >= 60 ? '🟡' : '🔴';
  md += `## Overall Readiness: ${readinessEmoji} ${overallReadiness}%\n\n`;
  
  // Show Owner Readiness and Business Attractiveness breakdown (12-question diagnostic)
  md += `## Readiness Breakdown\n\n`;
  
  // Use stored scores if available, otherwise use calculated
  const ownerScore = storedScores?.diagOwnerAnswers?.length > 0 
    ? Math.round((storedScores.diagOwnerAnswers.filter(v => v > 0).reduce((a,b) => a+b, 0) / 
        (storedScores.diagOwnerAnswers.filter(v => v > 0).length * 6)) * 100)
    : assessment.ownerReadinessScore || 0;
  const businessScore = storedScores?.diagBusinessAnswers?.length > 0
    ? Math.round((storedScores.diagBusinessAnswers.filter(v => v > 0).reduce((a,b) => a+b, 0) / 
        (storedScores.diagBusinessAnswers.filter(v => v > 0).length * 6)) * 100)
    : assessment.buyerAttractivenessScore || 0;
  
  const ownerStatus = ownerScore >= 80 ? '🟢 Strong' : ownerScore >= 60 ? '🟡 Developing' : '🔴 Needs Work';
  const businessStatus = businessScore >= 80 ? '🟢 Strong' : businessScore >= 60 ? '🟡 Developing' : '🔴 Needs Work';
  
  md += `**Owner Readiness:** ${ownerScore}% — ${ownerStatus}\n\n`;
  md += `_Your personal and financial readiness for transition_\n\n`;
  md += `**Business Attractiveness:** ${businessScore}% — ${businessStatus}\n\n`;
  md += `_How attractive your business is to potential buyers_\n\n`;
  
  // Gaps Identified with full context
  if (assessment.gaps.length > 0) {
    md += `---\n\n`;
    md += `## Key Gaps Identified\n\n`;
    md += `_The following areas need attention based on your responses:_\n\n`;
    
    const criticalGaps = assessment.gaps.filter(g => g.priority === 'critical');
    const highGaps = assessment.gaps.filter(g => g.priority === 'high');
    const mediumGaps = assessment.gaps.filter(g => g.priority === 'medium');
    
    const renderGap = (g) => {
      let gapMd = `**${g.question}**\n\n`;
      gapMd += `> Your answer: *${g.answer || 'No'}*\n\n`;
      if (g.action) {
        const recommendation = GAP_ACTION_RECOMMENDATIONS[g.action] || g.action;
        gapMd += `📋 **Recommended Action:** ${recommendation}\n\n`;
      }
      return gapMd;
    };
    
    if (criticalGaps.length > 0) {
      md += `### 🔴 Critical Priority\n\n`;
      md += `_These gaps pose significant risk to your succession plan._\n\n`;
      criticalGaps.forEach(g => {
        md += renderGap(g);
      });
    }
    
    if (highGaps.length > 0) {
      md += `### 🟡 High Priority\n\n`;
      md += `_Address these gaps within the next 6-12 months._\n\n`;
      highGaps.forEach(g => {
        md += renderGap(g);
      });
    }
    
    if (mediumGaps.length > 0) {
      md += `### 🟢 Medium Priority\n\n`;
      md += `_Plan to address these gaps as part of your ongoing succession planning._\n\n`;
      mediumGaps.forEach(g => {
        md += renderGap(g);
      });
    }
  } else {
    md += `---\n\n`;
    md += `## ✅ No Critical Gaps Identified\n\n`;
    md += `Great work! Your succession readiness is strong across all categories.\n\n`;
  }
  
  // Next Steps
  md += `---\n\n`;
  md += `## Next Steps\n\n`;
  md += `1. Review the gaps identified above with your advisor\n`;
  md += `2. Prioritize actions based on criticality and your timeline\n`;
  md += `3. Update your succession plan as you complete each action\n`;
  md += `4. Schedule quarterly reviews to track progress\n\n`;
  
  return md;
};

/**
 * Generate Succession Plan Document (Markdown)
 */
export const generatePlanDocument = (progress, userName = 'User', companyName = 'Practice') => {
  const sections = groupBySection(progress);
  const { planTemplate } = progress;
  
  // Header section (marked for centering)
  let md = `<!-- HEADER_START -->\n`;
  md += `# Succession Plan\n\n`;
  md += `### ${userName}\n\n`;
  md += `#### ${companyName}\n\n`;
  md += `**Generated:** ${new Date().toLocaleDateString()}  \n`;
  md += `**Plan Version:** ${planTemplate?.version || '1.0'}\n`;
  md += `<!-- HEADER_END -->\n\n`;
  md += `---\n\n`;
  
  // Executive Summary
  md += `## Executive Summary\n\n`;
  md += generateExecutiveSummary(sections, userName, companyName);
  md += `\n\n---\n\n`;
  
  // Table of Contents
  md += `## Table of Contents\n\n`;
  const sortedSections = Object.values(sections)
    .filter(s => s.questions.length > 0)
    .sort((a, b) => a.order - b.order);
  
  sortedSections.forEach((section, idx) => {
    md += `${idx + 1}. ${section.title}\n`;
  });
  md += `\n---\n\n`;
  
  // Generate each section
  sortedSections.forEach((section, idx) => {
    // Add extra spacing before section header
    md += `\n## ${idx + 1}. ${section.title}\n\n`;
    if (section.description) {
      md += `_${section.description}_\n\n`;
    }
    
    section.questions.forEach((q) => {
      // Question as bold text
      md += `**${q.questionText}**\n\n`;
      
      // Format the answer based on widget type
      if (q.widgetType === 'textarea') {
        // Block quote for long text
        md += `> ${q.formattedAnswer.replace(/\n/g, '\n> ')}\n\n`;
      } else if (q.widgetType === 'checklist') {
        // Bullet list for checklists
        const items = Array.isArray(q.answer?.value) ? q.answer.value : [q.formattedAnswer];
        items.forEach(item => {
          md += `- ${item}\n`;
        });
        md += `\n`;
      } else if (q.widgetType === 'contact') {
        // Formatted contact info
        md += `_${q.formattedAnswer.replace(/\n/g, ', ')}_\n\n`;
      } else {
        // Inline for simple values
        md += `_${q.formattedAnswer}_\n\n`;
      }
      
      // Horizontal rule for visual separation between Q&A pairs
      md += `---\n\n`;
    });
  });
  
  // Footer
  md += `\n_This document is a living plan. Return to the PlanBuilder to update your answers as your situation evolves._\n`;
  
  return md;
};

/**
 * Generate combined export data
 */
export const generateCombinedExport = (progress, userName = 'User', companyName = 'Practice') => {
  return {
    assessmentReport: generateAssessmentReport(progress, userName),
    planDocument: generatePlanDocument(progress, userName, companyName),
    metadata: {
      generatedAt: new Date().toISOString(),
      planName: progress.planTemplate?.name,
      completionPercentage: progress.completionPercentage,
      status: progress.status
    }
  };
};

/**
 * Get structured document data (for React components)
 */
export const getDocumentStructure = (progress, userName = 'User', companyName = 'Practice') => {
  const sections = groupBySection(progress);
  const assessment = calculateAssessmentScores(progress);
  
  return {
    header: {
      title: 'Succession Plan',
      userName,
      companyName,
      generatedDate: new Date().toLocaleDateString(),
      version: progress.planTemplate?.version || '1.0',
      status: progress.status,
      completionPercentage: progress.completionPercentage
    },
    assessment,
    sections: Object.values(sections)
      .filter(s => s.questions.length > 0)
      .sort((a, b) => a.order - b.order)
      .map(section => ({
        key: section.key,
        title: section.title,
        description: section.description,
        questions: section.questions.map(q => ({
          id: q.id,
          text: q.questionText,
          widgetType: q.widgetType,
          answer: q.answer,
          formattedAnswer: q.formattedAnswer
        }))
      }))
  };
};

/**
 * Get Assessment Report statistics
 */
export const getAssessmentStats = (progress, storedScores = null) => {
  const assessment = calculateAssessmentScores(progress);
  const categories = new Set();
  
  const { planTemplate, userPlanResponses } = progress;
  
  if (planTemplate?.planModules) {
    planTemplate.planModules.forEach(module => {
      module.planSections?.forEach(section => {
        section.planQuestions?.forEach(question => {
          if ((question.purpose === 'assessment' || question.purpose === 'both') && question.category) {
            categories.add(question.category);
          }
        });
      });
    });
  }
  
  // Use stored score from backend if available (source of truth)
  let overallReadiness = assessment.overallReadiness;
  if (storedScores?.vmiReadinessScore != null && storedScores.vmiReadinessScore > 0) {
    overallReadiness = storedScores.vmiReadinessScore;
  }
  
  return {
    categoryCount: categories.size || 5,
    gapCount: assessment.gaps.length,
    recommendationCount: assessment.gaps.length,
    overallReadiness,
    vmiScores: assessment.vmiScores
  };
};

/**
 * Get Plan Document statistics
 */
export const getPlanStats = (progress) => {
  const sections = new Set();
  let wordCount = 0;
  let questionCount = 0;
  
  const { planTemplate, userPlanResponses } = progress;
  
  // Count unique output sections
  if (planTemplate?.planModules) {
    planTemplate.planModules.forEach(module => {
      module.planSections?.forEach(section => {
        section.planQuestions?.forEach(question => {
          if (question.outputSection) {
            sections.add(question.outputSection);
          }
        });
      });
    });
  }
  
  // Calculate word count from responses
  if (userPlanResponses) {
    userPlanResponses.forEach(response => {
      questionCount++;
      const value = response.answer?.value;
      if (value) {
        const text = typeof value === 'string' ? value : JSON.stringify(value);
        wordCount += text.split(/\s+/).filter(w => w.length > 0).length;
      }
    });
  }
  
  // Estimate page count (approximately 400 words per page for formatted documents)
  const pageEstimate = Math.max(1, Math.ceil(wordCount / 400));
  
  return {
    sectionCount: sections.size || Object.keys(SECTION_CONFIG).length,
    wordCount,
    pageEstimate,
    questionCount
  };
};

/**
 * Generate Assessment Report as Markdown string
 */
export const generateAssessmentReportMarkdown = (progress, userName = 'User', storedScores = null) => {
  return generateAssessmentReport(progress, userName, storedScores);
};

/**
 * Generate Plan Document as Markdown string
 */
export const generatePlanDocumentMarkdown = (progress, userName = 'User', companyName = 'Practice') => {
  return generatePlanDocument(progress, userName, companyName);
};

/**
 * Legacy: Generate simple Q&A markdown (backward compatible)
 */
export const generatePlanMarkdown = (progress) => {
  if (!progress || !progress.planTemplate) return '';

  const { planTemplate, userPlanResponses } = progress;

  // Create answer map
  const answerMap = {};
  if (userPlanResponses) {
    userPlanResponses.forEach(r => {
      answerMap[r.planQuestionId] = r;
    });
  }

  let markdown = `# ${planTemplate.name}\n\n`;
  
  if (progress.status === 'completed') {
    markdown += `**Status:** Completed\n`;
  } else {
    markdown += `**Status:** In Progress (${progress.completionPercentage}%)\n`;
  }
  markdown += `**Last Updated:** ${new Date().toLocaleDateString()}\n\n`;
  markdown += `---\n\n`;

  if (planTemplate.planModules) {
    planTemplate.planModules.forEach(mod => {
      let moduleHasAnswers = false;
      let moduleMarkdown = `## ${mod.title}\n\n`;
      if (mod.description) moduleMarkdown += `_${mod.description}_\n\n`;

      if (mod.planSections) {
        mod.planSections.forEach(section => {
          let sectionHasAnswers = false;
          let sectionMarkdown = `### ${section.title}\n\n`;
          
          if (section.planQuestions) {
            section.planQuestions.forEach(question => {
              const response = answerMap[question.id];
              if (response) {
                 moduleHasAnswers = true;
                 sectionHasAnswers = true;
                 sectionMarkdown += `**${question.questionText}**\n\n`;
                 
                 const displayValue = formatAnswerValue(response.answer, question.widgetType);
                 sectionMarkdown += `> ${displayValue || '(No answer provided)'}\n\n`;
              }
            });
          }
          if (sectionHasAnswers) {
              moduleMarkdown += sectionMarkdown;
          }
        });
      }
      
      if (moduleHasAnswers) {
        markdown += moduleMarkdown;
        markdown += `\n`;
      }
    });
  }

  return markdown;
};

/**
 * Generate plan document based on plan type
 * 
 * Routes to specialized generators for non-succession plans:
 * - catastrophic: Legal agreement template
 * - continuity: Business Continuity Plan template
 * - succession (default): Q&A format with sections
 * 
 * @param {Object} progress - Plan progress with template and responses
 * @param {string} userName - User's name
 * @param {string} companyName - Company name
 * @returns {string} Markdown document content
 */
export const generatePlanDocumentByType = (progress, userName = 'User', companyName = 'Practice') => {
  const planType = progress?.planTemplate?.planType;
  
  if (planType === 'catastrophic') {
    const formData = mapPlanBuilderToCatastrophicFormat(progress);
    return generateCatastrophicPlanDocument(formData);
  }
  
  if (planType === 'continuity') {
    const formData = mapPlanBuilderToBCPFormat(progress);
    return generateBCPDocument(formData);
  }
  
  // Default: succession plan or unspecified type
  return generatePlanDocument(progress, userName, companyName);
};

/**
 * Check if a plan type supports assessment reports
 * @param {string} planType - The plan type
 * @returns {boolean} Whether the plan type has an assessment component
 */
export const planTypeHasAssessment = (planType) => {
  // Only succession plans have assessment components
  return planType === 'succession' || planType === null || planType === undefined;
};

/**
 * Get display name for a plan type
 * @param {string} planType - The plan type
 * @returns {string} Human-readable plan type name
 */
export const getPlanTypeName = (planType) => {
  const names = {
    succession: 'Succession Plan',
    catastrophic: 'Catastrophic & Continuity Plan',
    continuity: 'Business Continuity Plan'
  };
  return names[planType] || 'Plan';
};

/**
 * DocumentGeneratorService object export for convenient access
 */
export const DocumentGeneratorService = {
  getDocumentStructure,
  generateAssessmentReport,
  generatePlanDocument,
  generateAssessmentReportMarkdown,
  generatePlanDocumentMarkdown,
  generatePlanDocumentByType,
  generateCombinedExport,
  generatePlanMarkdown,
  getAssessmentStats,
  getPlanStats,
  planTypeHasAssessment,
  getPlanTypeName
};
